import { useMemo } from "react";
import { useSelector } from "react-redux";
import { BrowserRouter, Navigate, Routes, Route } from "react-router-dom";
import { Toaster } from "react-hot-toast";
import { CssBaseline, ThemeProvider } from "@mui/material";
import { createTheme } from "@mui/material/styles";
import { themeSettings } from "./theme";
import { AuthProvider } from 'contexts/AuthProvider';
import { DateRangeProvider } from 'contexts/DateRangeContext';
import { isTokenExpired } from 'hooks/useAuthCheck';
import { DEFAULT_PAGE } from 'state/constants';
import NotFound from "components/NotFound";
import Forbidden from "components/Forbidden";
import Layout from "scenes/layout";
import HomePage from "scenes/homePage";
import FlightGraph from "scenes/homePage/flightGraph";
import About from "scenes/about";
import Contacts from "scenes/contacts";
import Contact from "scenes/contacts/Contact";
import LoginPage from "scenes/login";
import SubLayout from 'scenes/layout/SubLayout';
import MainPage from "scenes/mainPage";
import Dashboard from "scenes/dashboard";
import OTPAnalytics from "scenes/otpAnalytics";
import MLAnalytics from "scenes/mlAnalytics";
import LoadFactor from "scenes/loadFactor";
import ShiftReports from "scenes/shiftReports";
import ShiftReportEdit from "scenes/shiftReports/ShiftReport";
import FlightSchedule from "scenes/flightSchedule";
import FlightEdit from "scenes/flightSchedule/Flight";
import FlightView from "scenes/flightView";
import FlightMap from "scenes/flightMap";
import StationSchedule from "scenes/stationSchedule";
import SSIM from "scenes/ssim";
import FlightGanttChart from "scenes/flightGanttChart";
import FlightSlots from "scenes/flightSlots";
import FlightChart from "scenes/charts/flightChart";
import FlightCompareChart from "scenes/charts/flightCompareChart";
import CrewComposition from "scenes/crewComposition";
import DailyFlightChart from "scenes/charts/dailyFlightChart";
import CancelledFlightChart from "scenes/charts/cancelledFlightChart";
import TATChart from "scenes/charts/tatChart";
import TATDiffChart from "scenes/charts/tatDiffChart";
import TaxiOutChart from "scenes/charts/taxiOutChart";
import TaxiInChart from "scenes/charts/taxiInChart";
import BTDiffChart from "scenes/charts/btDiffChart";
import AirTimeChart from "scenes/charts/airTimeChart";
import BlockMinutesPer1FltChart from "scenes/charts/blockMinutesPer1FltChart";
import TotalBlockHoursChart from "scenes/charts/totalBlockHoursChart";
import CrewPairings from "scenes/crewPairings";
import CrewPairingCountChart from "scenes/charts/crewPairingCountChart";
import DailyCrewPairingChart from "scenes/charts/dailyCrewPairingChart";
import CrewDashboard from "scenes/crewDashboard";
import CrewMembers from "scenes/crewMembers";
import CrewMemberEdit from "scenes/crewMembers/CrewMember";
import NonCrewMembers from "scenes/nonCrewMembers";
import NonCrewMemberEdit from "scenes/nonCrewMembers/NonCrewMember";
import JumpSeaters from "scenes/jumpSeaters";
import Deadheadings from "scenes/deadheadings";
import CrewRoutes from "scenes/crewRoutes";
import DispatcherChecklists from "scenes/dispatcherChecklists";
import AircraftRoutes from "scenes/aircraftRoutes";
import CrewShuttles from "scenes/crewShuttles";
import UnfinalizedCrewRoutes from "scenes/unfinalizedCrewRoutes";
import CrewPendingNotifications from "scenes/crewPendingNotifications";
import OTPByCrew from "./scenes/otpByCrew";
import TopBuddyFlying from "scenes/topBuddyFlying";
import RosterAnalytics from "scenes/rosterAnalytics";
import RosterAccumulates from "scenes/rosterAccumulates";
import AnnualLeaves from "scenes/annualLeaves";
import CrewRequests from "scenes/crewRequests";
import CrewRequestEdit from "scenes/crewRequests/CrewRequest";
import RosterDetails from "scenes/rosterDetails";
import CrewGanttChart from "scenes/crewGanttChart";
import CrewRouteCountChart from "scenes/charts/crewRouteCountChart";
import DailyCrewRouteChart from "scenes/charts/dailyCrewRouteChart";
import CrewMembersCountChart from "scenes/charts/crewMembersCountChart";
import FlyingCrewCountChart from "scenes/charts/flyingCrewCountChart";
import DeadHeadingsChart from "scenes/charts/deadheadingsChart";
import NonCrewListChart from "scenes/charts/nonCrewListChart";
import CrewBlockHoursChart from "scenes/charts/crewBlockHoursChart";
import CrewTotalHoursChart from "scenes/charts/crewTotalHoursChart";
import RosterDetailsChart from "scenes/charts/rosterDetailsChart";
import Job255CrewRoutes from "scenes/job255";
import Job255CrewRouteCountChart from "scenes/job255/charts/crewRouteCountChart";
import Job255DailyCrewRouteChart from "scenes/job255/charts/dailyCrewRouteChart";
import Job255CrewBlockHoursChart from "scenes/job255/charts/crewBlockHoursChart";
import CrewPayroll from "scenes/crewPayroll";
import Job147CrewPayroll from "scenes/crewPayroll/Job147";
import IncomeAdjustments from "scenes/incomeAdjustment";
import IncomeAdjustmentEdit from "scenes/incomeAdjustment/IncomeAdjustment";
import SalesChart from "scenes/inflightSales/charts";
import CrewList from "scenes/inflightSales/CrewList";
import CrewMemberPSW from "scenes/inflightSales/CrewMemberPSW";
import FlightList from "scenes/inflightSales/FlightList";
import Invoices from "scenes/inflightSales/Invoices";
import Inventory from "scenes/inflightSales/Inventory";
import ProductSales from "scenes/inflightSales/ProductSales";
import ProductInventoryByDate from "scenes/inflightSales/ProductInventoryByDate";
import ProductInventoryBySectorType from "scenes/inflightSales/ProductInventoryBySectorType";
import ProductInventorySummary from "scenes/inflightSales/ProductInventorySummary";
import CrewMemberSales from "scenes/inflightSales/CrewMemberSales";
import DuplicateInvoicedFlights from "scenes/inflightSales/DuplicateInvoicedFlights";
import Employees from "scenes/employees";
import EmployeeDetail from "scenes/employees/EmployeeDetail";
import EmployeeStats from "scenes/employeeStats";
import Timekeeping from "scenes/timekeeping";
import Posts from "scenes/posts";
import ProfilePage from "scenes/profilePage";
import KanbanBoard from "scenes/kanbanBoard";
import FileManager from "scenes/fileManager";
import FAQViewer from "scenes/faq";
import FlightScheduleChanges from "scenes/flightScheduleChanges";
import CrewScheduleChanges from "scenes/crewScheduleChanges";
import AnomalyDetection from "scenes/anomalyDetection";
import Users from "scenes/users";
import UserEdit from "scenes/users/User";
import Settings from "scenes/settings";
import SetPermissions from "scenes/users/SetPermissions";
import PurgeData from "scenes/purgeData";
import DataImporter from "scenes/dataImporter";
import GenerateLicense from "scenes/CMS/generateLicense";
import Roles from "scenes/CMS/roles";
import RoleEdit from "scenes/CMS/roles/Role";
import Permissions from "scenes/CMS/permissions";
import PermissionEdit from "scenes/CMS/permissions/Permission";
import Menus from "scenes/CMS/menus";
import MenuEdit from "scenes/CMS/menus/Menu";
import TatDashboard from "scenes/tatDashboard";
import TatController from "scenes/tatController";
import TatInstances from "scenes/tatInstances";
import TatTemplates from "scenes/CMS/tatTemplates";
import TatTemplateEdit from "scenes/CMS/tatTemplates/TatTemplate";
import Todos from "scenes/CMS/todos";
import TodoEdit from "scenes/CMS/todos/Todo";
import Holidays from "scenes/CMS/holidays";
import HolidayEdit from "scenes/CMS/holidays/Holiday";
import Departments from "scenes/CMS/departments";
import DepartmentEdit from "scenes/CMS/departments/Department";
import Divisions from "scenes/CMS/divisions";
import DivisionEdit from "scenes/CMS/divisions/Division";
import DeptUnits from "scenes/CMS/deptunits";
import DeptUnitEdit from "scenes/CMS/deptunits/DeptUnit";
import Ranks from "scenes/CMS/ranks";
import RankEdit from "scenes/CMS/ranks/Rank";
import JobTitles from "scenes/CMS/jobTitles";
import JobTitleEdit from "scenes/CMS/jobTitles/JobTitle";
import JobGrades from "scenes/CMS/jobGrades";
import JobGradeEdit from "scenes/CMS/jobGrades/JobGrade";
import FAQs from "scenes/CMS/faqs";
import FAQEdit from "scenes/CMS/faqs/FAQ";
import HomepageEditor from "scenes/CMS/homepageEditor";
import ACTypes from "scenes/CMS/acTypes";
import ACTypeEdit from "scenes/CMS/acTypes/ACType";
import Shifts from "scenes/CMS/shifts";
import ShiftEdit from "scenes/CMS/shifts/Shift";
import Aircrafts from "scenes/CMS/aircrafts";
import AircraftEdit from "scenes/CMS/aircrafts/Aircraft";
import TransportPlaces from "scenes/CMS/transportPlaces";
import TransportPlaceEdit from "scenes/CMS/transportPlaces/TransportPlace";
import Airports from "scenes/CMS/airports";
import AirportEdit from "scenes/CMS/airports/Airport";
import AirportPairs from "scenes/CMS/airportPairs";
import AirportPairEdit from "scenes/CMS/airportPairs/AirportPair";
import CrewComplements from "scenes/CMS/crewComplements";
import CrewComplementEdit from "scenes/CMS/crewComplements/CrewComplement";
import Countries from "scenes/CMS/countries";
import CountryEdit from "scenes/CMS/countries/Country";
import DelayCodes from "scenes/CMS/delayCodes";
import DelayCodeEdit from "scenes/CMS/delayCodes/DelayCode";
import CrewPerformances from "scenes/crewPerformances";
import CrewPerformanceEdit from "scenes/crewPerformances/CrewPerformance";
import TravelAuthorizationForms from "scenes/travelAuthorizationForm";
import TravelAuthorizationFormEdit from "scenes/travelAuthorizationForm/TravelAuthorizationForm";
import TravelAllowanceEntries from "scenes/travelAllowanceEntry";
import TravelAllowanceEntryEdit from "scenes/travelAllowanceEntry/TravelAllowanceEntryForm";
import FixedBlockTimes from "scenes/CMS/fixedBlockTimes";
import FixedBlockTimeEdit from "scenes/CMS/fixedBlockTimes/FixedBlockTime";
import CostItems from "scenes/CMS/costItems";
import CostItemEdit from "scenes/CMS/costItems/CostItem";
import ContributionTypes from "scenes/CMS/contributionTypes";
import ContributionTypeEdit from "scenes/CMS/contributionTypes/ContributionType";
import Contributions from "scenes/CMS/contributions";
import ContributionEdit from "scenes/CMS/contributions/Contribution";
import TravelAllowances from "scenes/CMS/travelAllowances";
import TravelAllowanceEdit from "scenes/CMS/travelAllowances/TravelAllowance";
import Params from "scenes/CMS/params";
import ParamEdit from "scenes/CMS/params/Param";
import Products from "scenes/CMS/products";
import ProductEdit from "scenes/CMS/products/Product";
import ProductCategories from "scenes/CMS/productCategories";
import ProductCategoryEdit from "scenes/CMS/productCategories/ProductCategory";
import Suppliers from "scenes/CMS/suppliers";
import SupplierEdit from "scenes/CMS/suppliers/Supplier";
import SupplyStandards from "scenes/CMS/supplyStandards";
import SupplyStandardEdit from "scenes/CMS/supplyStandards/SupplyStandard";
import CrewScheduleCodes from "scenes/CMS/crewScheduleCodes";
import CrewScheduleCodeEdit from "scenes/CMS/crewScheduleCodes/CrewScheduleCode";
import CrewScheduleCodeCategories from "scenes/CMS/crewScheduleCodeCategories";
import CrewScheduleCodeCategoryEdit from "scenes/CMS/crewScheduleCodeCategories/CrewScheduleCodeCategory";
import ChecklistItems from "scenes/CMS/checklistItems";
import ChecklistItemEdit from "scenes/CMS/checklistItems/ChecklistItem";
import Notifications from "scenes/notifications";
import NoticeBoard from "scenes/notifications/NoticeBoard";
import TrainingCourses from "scenes/trainingCourses";
import TrainingCourseEdit from "scenes/trainingCourses/TrainingCourse";
import TrainingSubjects from "scenes/CMS/trainingSubjects";
import TrainingSubjectEdit from "scenes/CMS/trainingSubjects/TrainingSubject";
import CompetencyFrameworks from "scenes/CMS/competencyFrameworks";
import CompetencyFrameworkEdit from "scenes/CMS/competencyFrameworks/CompetencyFramework";
import TrainingPlanner from "scenes/trainingPlanner";
import TrainingSessions from "scenes/trainingSessions";
import TrainingSessionEdit from "scenes/trainingSessions/TrainingSession";
import ScheduleSeasons from "scenes/scheduleSeasons";
import SchedulePlanner from "scenes/schedulePlanner";
import NetworkPlanner from "scenes/networkPlanner";
import RotationBuilder from "scenes/rotationBuilder";
import StationView from "scenes/stationView";
import DataRequests from "scenes/dataRequests";
import DynamicList from "scenes/dynamicData/dynamicForm/DynamicList";
import DynamicEdit from "scenes/dynamicData/dynamicForm/DynamicEdit";
import CollectionDefs from "scenes/dynamicData/collections";
import CollectionDefPage from "scenes/dynamicData/collections/CollectionDefPage";


function App() {
  const token = useSelector((state) => state.token);
  const user = useSelector((state) => state.user);
  const mode = useSelector((state) => state.mode);
  const theme = useMemo(() => createTheme(themeSettings(mode)), [mode]);   
  
  const ProtectedRoute = useMemo(() => ({ element, paths = [] }) => {
    if (!token || isTokenExpired(token)) 
      return <Navigate to="/auth/login" replace />;

    if (user?.role === 'admin') {
      return element;
    }

    const hasPermission = !paths?.length || 
      paths?.every((path) =>
        user.permissions?.some((p) => path?.startsWith(p?.path))
      );
    
    return hasPermission ? element : <Navigate to="/403" />;
  }, [token, user]); 

  const renderRoute = (path, IndexComponent, EditComponent, idParam) => {
    return (
      <Route 
        key={path}
        path={path} 
        element={
          <ProtectedRoute
            element={<SubLayout />}
            paths={[path, `${path}/new`, `${path}/:${idParam}`]}
          />
        }
      >  
        {/* Route List */}
        <Route index element={<IndexComponent />} />        
        {/* Route New */}
        <Route path="new" element={<EditComponent />} />        
        {/* Route Edit by ID */}
        <Route path={`:${idParam}`} element={<EditComponent />} />
      </Route>
    );
  };


  return (
    <div className="app">
      <BrowserRouter>
        <AuthProvider>
          <DateRangeProvider>
            <ThemeProvider theme={theme}>
              <CssBaseline />
              <Toaster
                position="top-center"
                reverseOrder={false}
                toastOptions={{                  
                  style: { transition: "none" },
                }}
              />
              <Routes>             
                {/* Redirect root */}
                <Route path="/" element={<Navigate to={DEFAULT_PAGE} replace />} />
                <Route path="/home" element={<HomePage />} />
                <Route path="/about" element={<About />} />                
                <Route path="/contact" element={<Contact />} />
                <Route path="/flightgraph" element={<FlightGraph />} />
                <Route path="/auth/login" element={<LoginPage />} />       
                           
                <Route element={<Layout />}>
                  <Route path="/settings" element={<Settings />} />
                  
                  {/* Main */}
                  <Route path="/main" element={<ProtectedRoute element={<MainPage />} paths={["main"]} />} />
                  <Route path="/dashboard" element={<ProtectedRoute element={<Dashboard />} paths={["dashboard"]} />} />
                  <Route path="/otp" element={<ProtectedRoute element={<OTPAnalytics />} paths={["otp"]} />} />
                  <Route path="/ml-analytics" element={<ProtectedRoute element={<MLAnalytics />} paths={["ml-analytics"]} />} />
                  <Route path="/loadfactor" element={<ProtectedRoute element={<LoadFactor />} paths={["loadfactor"]} />} />
                  {renderRoute("shiftreports", ShiftReports, ShiftReportEdit, "reportId")}
                  
                  {/* Notice Board */}
                  <Route path="/notifications" element={<ProtectedRoute element={<Notifications />} paths={["notifications"]} />} />
                  <Route path="/noticeboard" element={<ProtectedRoute element={<NoticeBoard />} paths={["noticeboard"]} />} />
                  
                  {/* Flight */}
                  {renderRoute("flightschedule", FlightSchedule, FlightEdit, "id")}                  
                  <Route path="/flightview" element={<ProtectedRoute element={<FlightView />} paths={["flightview"]} />} />
                  <Route path="/flightmap" element={<ProtectedRoute element={<FlightMap />} paths={["flightmap"]} />} />
                  <Route path="/stationschedule" element={<ProtectedRoute element={<StationSchedule />} paths={["stationschedule"]} />} />
                  <Route path="/flightganttchart" element={<ProtectedRoute element={<FlightGanttChart />} paths={["flightganttchart"]} />} />
                  <Route path="/flightslots" element={<ProtectedRoute element={<FlightSlots />} paths={["flightslots"]} />} />
                  <Route path="/ssim" element={<ProtectedRoute element={<SSIM />} paths={["ssim"]} />} />
                  <Route path="/flightcompare" element={<ProtectedRoute element={<FlightCompareChart />} paths={["flightcompare"]} />} />
                  <Route path="/flight" element={<ProtectedRoute element={<FlightChart />} paths={["flight"]} />} />
                  <Route path="/dailyflight" element={<ProtectedRoute element={<DailyFlightChart />} paths={["dailyflight"]} />} />
                  <Route path="/cancelledflight" element={<ProtectedRoute element={<CancelledFlightChart />} paths={["cancelledflight"]} />} />
                  <Route path="/tat" element={<ProtectedRoute element={<TATChart />} paths={["tat"]} />} />
                  <Route path="/tatdiff" element={<ProtectedRoute element={<TATDiffChart />} paths={["tatdiff"]} />} />
                  <Route path="/taxiout" element={<ProtectedRoute element={<TaxiOutChart />} paths={["taxiout"]} />} />
                  <Route path="/taxiin" element={<ProtectedRoute element={<TaxiInChart />} paths={["taxiin"]} />} />
                  <Route path="/btdiff" element={<ProtectedRoute element={<BTDiffChart />} paths={["btdiff"]} />} />                  
                  <Route path="/airtime" element={<ProtectedRoute element={<AirTimeChart />} paths={["airtime"]} />} />                  
                  <Route path="/blockminutesper1flt" element={<ProtectedRoute element={<BlockMinutesPer1FltChart />} paths={["blockminutesper1flt"]} />} />                  
                  <Route path="/blockhours" element={<ProtectedRoute element={<TotalBlockHoursChart />} paths={["blockhours"]} />} />                  
                  <Route path="/crewcomposition" element={<ProtectedRoute element={<CrewComposition />} paths={["crewcomposition"]} />} />
                  <Route path="/crewpairings" element={<ProtectedRoute element={<CrewPairings />} paths={["crewpairings"]} />} />
                  <Route path="/crewpairingscount" element={<ProtectedRoute element={<CrewPairingCountChart />} paths={["crewpairingscount"]} />} />
                  <Route path="/dailycrewpairings" element={<ProtectedRoute element={<DailyCrewPairingChart />} paths={["dailycrewpairings"]} />} />

                  {/* Turnaround Time */}
                  <Route path="/tatdashboard" element={<ProtectedRoute element={<TatDashboard />} paths={["tatdashboard"]} />} />
                  <Route path="/tatcontroller" element={<ProtectedRoute element={<TatController />} paths={["tatcontroller"]} />} />
                  <Route path="/tatinstances" element={<ProtectedRoute element={<TatInstances />} paths={["tatinstances"]} />} />
                  {renderRoute("tattemplates", TatTemplates, TatTemplateEdit, "tatTemplateId")}
                  
                  {/* Crew */}
                  <Route path="/crewdashboard" element={<ProtectedRoute element={<CrewDashboard />} paths={["crewdashboard"]} />} />
                  {renderRoute("crewmembers", CrewMembers, CrewMemberEdit, "id")}
                  {renderRoute("noncrewmembers", NonCrewMembers, NonCrewMemberEdit, "id")}                  
                  <Route path="/jumpseaters" element={<ProtectedRoute element={<JumpSeaters />} paths={["jumpseaters"]} />} />
                  <Route path="/deadheadings" element={<ProtectedRoute element={<Deadheadings />} paths={["deadheadings"]} />} />
                  <Route path="/crewroutes" element={<ProtectedRoute element={<CrewRoutes />} paths={["crewroutes"]} />} />
                  <Route path="/dispatcherchecklists" element={<ProtectedRoute element={<DispatcherChecklists />} paths={["dispatcherchecklists"]} />} />
                  <Route path="/aircraftroutes" element={<ProtectedRoute element={<AircraftRoutes />} paths={["aircraftroutes"]} />} />
                  <Route path="/unfinalizedcrewroutes" element={<ProtectedRoute element={<UnfinalizedCrewRoutes />} paths={["unfinalizedcrewroutes"]} />} />
                  <Route path="/crewshuttles" element={<ProtectedRoute element={<CrewShuttles />} paths={["crewshuttles"]} />} />
                  <Route path="/crewpendingnotifications" element={<ProtectedRoute element={<CrewPendingNotifications />} paths={["crewpendingnotifications"]} />} />
                  <Route path="/otpbycrew" element={<ProtectedRoute element={<OTPByCrew />} paths={["otpbycrew"]} />} />
                  <Route path="/topbuddyflying" element={<ProtectedRoute element={<TopBuddyFlying />} paths={["topbuddyflying"]} />} />
                  <Route path="/rosteranalytics" element={<ProtectedRoute element={<RosterAnalytics />} paths={["rosteranalytics"]} />} />
                  <Route path="/rosteraccumulates" element={<ProtectedRoute element={<RosterAccumulates />} paths={["rosteraccumulates"]} />} />
                  <Route path="/annualleaves" element={<ProtectedRoute element={<AnnualLeaves />} paths={["annualleaves"]} />} />
                  <Route path="/rosterdetails" element={<ProtectedRoute element={<RosterDetails />} paths={["rosterdetails"]} />} />
                  <Route path="/crewganttchart" element={<ProtectedRoute element={<CrewGanttChart />} paths={["crewganttchart"]} />} />
                  {renderRoute("crewrequests", CrewRequests, CrewRequestEdit, "requestId")}                  
                  <Route path="/crewroutescount" element={<ProtectedRoute element={<CrewRouteCountChart />} paths={["crewroutescount"]} />} />
                  <Route path="/dailycrewroutes" element={<ProtectedRoute element={<DailyCrewRouteChart />} paths={["dailycrewroutes"]} />} />
                  <Route path="/crewmemberscount" element={<ProtectedRoute element={<CrewMembersCountChart />} paths={["crewmemberscount"]} />} />
                  <Route path="/flyingcrewcount" element={<ProtectedRoute element={<FlyingCrewCountChart />} paths={["flyingcrewcount"]} />} />
                  <Route path="/deadheadingschart" element={<ProtectedRoute element={<DeadHeadingsChart />} paths={["deadheadingschart"]} />} />
                  <Route path="/noncrewlistchart" element={<ProtectedRoute element={<NonCrewListChart />} paths={["noncrewlistchart"]} />} />
                  <Route path="/crewblockhours" element={<ProtectedRoute element={<CrewBlockHoursChart />} paths={["crewblockhours"]} />} />
                  <Route path="/crewtotalhours" element={<ProtectedRoute element={<CrewTotalHoursChart />} paths={["crewtotalhours"]} />} />
                  <Route path="/crewrosterdetails" element={<ProtectedRoute element={<RosterDetailsChart />} paths={["crewrosterdetails"]} />} />
                  
                  {/* Job 255 */}
                  <Route path="/job255crewroutes" element={<ProtectedRoute element={<Job255CrewRoutes />} paths={["job255crewroutes"]} />} />                  
                  <Route path="/job255crewroutescount" element={<ProtectedRoute element={<Job255CrewRouteCountChart />} paths={["job255crewroutescount"]} />} />
                  <Route path="/job255dailycrewroutes" element={<ProtectedRoute element={<Job255DailyCrewRouteChart />} paths={["job255dailycrewroutes"]} />} />
                  <Route path="/job255crewblockhours" element={<ProtectedRoute element={<Job255CrewBlockHoursChart />} paths={["job255crewblockhours"]} />} />
                                   
                  {/* Crew Payroll */}
                  <Route path="/crewpayroll" element={<ProtectedRoute element={<CrewPayroll />} paths={["crewpayroll"]} />} />                  
                  <Route path="/job147crewpayroll" element={<ProtectedRoute element={<Job147CrewPayroll />} paths={["job147crewpayroll"]} />} />                  
                  {renderRoute("incomeadjustments", IncomeAdjustments, IncomeAdjustmentEdit, "incomeAdjustmentId")}
                  {renderRoute("crewperformances", CrewPerformances, CrewPerformanceEdit, "crewPerformanceId")}
                  {renderRoute("travelauthorizationforms", TravelAuthorizationForms, TravelAuthorizationFormEdit, "travelAuthorizationFormId")}
                  {renderRoute("travelallowanceentries", TravelAllowanceEntries, TravelAllowanceEntryEdit, "travelAllowanceEntryId")}
                  {renderRoute("fixedblocktimes", FixedBlockTimes, FixedBlockTimeEdit, "fixedBlockTimeId")}
                  {renderRoute("costitems", CostItems, CostItemEdit, "costItemId")}
                  {renderRoute("contributiontypes", ContributionTypes, ContributionTypeEdit, "contributionTypeId")}
                  {renderRoute("contributions", Contributions, ContributionEdit, "contributionId")}
                  {renderRoute("travelallowances", TravelAllowances, TravelAllowanceEdit, "travelAllowanceId")}
                                    
                  {/* Sales */}
                  <Route path="/saleschart" element={<ProtectedRoute element={<SalesChart />} paths={["saleschart"]} />} />
                  <Route 
                    path="crewlist" 
                    element={
                      <ProtectedRoute
                        element={<SubLayout />}
                        paths={["crewlist", "crewlist/:crewCode"]}
                      />
                    }
                  >                  
                    <Route index element={<CrewList />} />
                    <Route path=":crewCode" element={<CrewMemberPSW />} />
                  </Route>
                  <Route path="/flightlist" element={<ProtectedRoute element={<FlightList />} paths={["flightlist"]} />} />
                  <Route path="/invoices" element={<ProtectedRoute element={<Invoices />} paths={["invoices"]} />} />
                  <Route path="/inventory" element={<ProtectedRoute element={<Inventory />} paths={["inventory"]} />} />
                  <Route path="/productsales" element={<ProtectedRoute element={<ProductSales />} paths={["productsales"]} />} />
                  <Route path="/crewmembersales" element={<ProtectedRoute element={<CrewMemberSales />} paths={["crewmembersales"]} />} />
                  <Route path="/productinventorybydate" element={<ProtectedRoute element={<ProductInventoryByDate />} paths={["productinventorybydate"]} />} />
                  <Route path="/productinventorybysectortype" element={<ProtectedRoute element={<ProductInventoryBySectorType />} paths={["productinventorybysectortype"]} />} />
                  <Route path="/productinventorysummary" element={<ProtectedRoute element={<ProductInventorySummary />} paths={["productinventorysummary"]} />} />
                  <Route path="/duplicateinvoicedflights" element={<ProtectedRoute element={<DuplicateInvoicedFlights />} paths={["duplicateinvoicedflights"]} />} />
                                   
                  {/* Schedule Planning */}
                  <Route path="/schedule-seasons" element={<ProtectedRoute element={<ScheduleSeasons />} paths={["schedule-seasons"]} />} />
                  <Route path="/scheduleplanner" element={<ProtectedRoute element={<SchedulePlanner />} paths={["scheduleplanner"]} />} />
                  <Route path="/networkplanner" element={<ProtectedRoute element={<NetworkPlanner />} paths={["networkplanner"]} />} />
                  <Route path="/rotationbuilder" element={<ProtectedRoute element={<RotationBuilder />} paths={["rotationbuilder"]} />} />
                  <Route path="/stationview" element={<ProtectedRoute element={<StationView />} paths={["stationview"]} />} />

                  {/* HRMS */}
                  {renderRoute("employees", Employees, EmployeeDetail, "employeeId")}
                  <Route path="/employeestats" element={<ProtectedRoute element={<EmployeeStats />} />} />
                  <Route path="/timekeeping" element={<ProtectedRoute element={<Timekeeping />} />} />
                  <Route path="/notifications" element={<ProtectedRoute element={<Notifications />} />} />

                  {/* TMS */}
                  {renderRoute("trainingcourses", TrainingCourses, TrainingCourseEdit, "trainingCourseId")}
                  <Route path="/trainingplanner" element={<ProtectedRoute element={<TrainingPlanner />} paths={["trainingplanner"]} />} />
                  {renderRoute("trainingsessions", TrainingSessions, TrainingSessionEdit, "trainingSessionId")}
                  {renderRoute("trainingsubjects", TrainingSubjects, TrainingSubjectEdit, "trainingSubjectId")}
                  {renderRoute("competencyframeworks", CompetencyFrameworks, CompetencyFrameworkEdit, "competencyFrameworkId")}

                  {/* Pages */}
                  <Route path="/posts" element={<ProtectedRoute element={<Posts />} />} />
                  <Route path="/profile/:userId" element={<ProtectedRoute element={<ProfilePage />} />} />
                  <Route path="/kanbanboard" element={<ProtectedRoute element={<KanbanBoard />} paths={["kanbanboard"]} />} />
                  <Route path="/filemanager" element={<ProtectedRoute element={<FileManager />} paths={["filemanager"]} />} />
                  <Route path="/faq" element={<ProtectedRoute element={<FAQViewer />} paths={["faq"]} />} />
                  
                  {/* CMS */}
                  <Route path="/homepageeditor" element={<ProtectedRoute element={<HomepageEditor />} paths={["homepageeditor"]} />} />
                  {renderRoute("faqs", FAQs, FAQEdit, "faqId")}                  
                  <Route path="/contacts" element={<Contacts />} />
                  {renderRoute("todos", Todos, TodoEdit, "todoId")}
                  {renderRoute("holidays", Holidays, HolidayEdit, "holidayId")}
                  {renderRoute("products", Products, ProductEdit, "productId")}
                  {renderRoute("productcategories", ProductCategories, ProductCategoryEdit, "productCategoryId")}
                  {renderRoute("suppliers", Suppliers, SupplierEdit, "supplierId")}
                  {renderRoute("supplystandards", SupplyStandards, SupplyStandardEdit, "supplyStandardId")}
                  {renderRoute("divisions", Divisions, DivisionEdit, "divisionId")}
                  {renderRoute("departments", Departments, DepartmentEdit, "departmentId")}
                  {renderRoute("deptunits", DeptUnits, DeptUnitEdit, "deptUnitId")}
                  {renderRoute("ranks", Ranks, RankEdit, "rankId")}
                  {renderRoute("jobtitles", JobTitles, JobTitleEdit, "jobTitleId")}
                  {renderRoute("jobgrades", JobGrades, JobGradeEdit, "jobGradeId")}
                  {renderRoute("actypes", ACTypes, ACTypeEdit, "acTypeId")}
                  {renderRoute("shifts", Shifts, ShiftEdit, "shiftId")}
                  {renderRoute("aircrafts", Aircrafts, AircraftEdit, "aircraftId")}
                  {renderRoute("transportplaces", TransportPlaces, TransportPlaceEdit, "transportPlaceId")}
                  {renderRoute("airports", Airports, AirportEdit, "airportId")}
                  {renderRoute("airportpairs", AirportPairs, AirportPairEdit, "airportPairId")}
                  {renderRoute("crewcomplements", CrewComplements, CrewComplementEdit, "crewComplementId")}
                  {renderRoute("countries", Countries, CountryEdit, "countryId")}
                  {renderRoute("delaycodes", DelayCodes, DelayCodeEdit, "delayCodeId")}
                  {renderRoute("crewschedulecodes", CrewScheduleCodes, CrewScheduleCodeEdit, "crewScheduleCodeId")}
                  {renderRoute("crewschedulecodecategories", CrewScheduleCodeCategories, CrewScheduleCodeCategoryEdit, "crewScheduleCodeCategoryId")}
                  {renderRoute("checklistitems", ChecklistItems, ChecklistItemEdit, "checklistItemId")}
                  
                  {/* Audit */}
                  <Route 
                    path="flightschedulechanges" 
                    element={
                      <ProtectedRoute
                        element={<SubLayout />}
                        paths={["flightschedulechanges"]}
                      />
                    }
                  >
                    <Route index element={<FlightScheduleChanges />} />
                  </Route>

                  <Route
                    path="crewschedulechanges"
                    element={
                      <ProtectedRoute
                        element={<SubLayout />}
                        paths={["crewschedulechanges"]}
                      />
                    }
                  >
                    <Route index element={<CrewScheduleChanges />} />
                  </Route>

                  <Route
                    path="anomalydetection"
                    element={
                      <ProtectedRoute
                        element={<SubLayout />}
                        paths={["anomalydetection"]}
                      />
                    }
                  >
                    <Route index element={<AnomalyDetection />} />
                  </Route>

                  {/* Auth */}                  
                  <Route path="users" element={<SubLayout />}>
                    <Route
                      index
                      element={<ProtectedRoute element={<Users />} paths={["users"]} />}
                    />
                    <Route
                      path="new"
                      element={<ProtectedRoute element={<UserEdit />} paths={["users/new"]} />}
                    />
                    <Route
                      path=":userId"
                      element={<ProtectedRoute element={<UserEdit />} paths={["users/:userId"]} />}
                    />
                    <Route
                      path="authorization/:userId"
                      element={<ProtectedRoute element={<SetPermissions />} paths={["users/authorization/:userId"]} />}
                    />
                  </Route>                    
                  {renderRoute("permissions", Permissions, PermissionEdit, "permissionId")}
                  {renderRoute("roles", Roles, RoleEdit, "roleId")}
                  {renderRoute("menus", Menus, MenuEdit, "menuId")}
                  {renderRoute("params", Params, ParamEdit, "paramId")} 
                  <Route 
                    path="/generatelicense" 
                    element={
                      <ProtectedRoute 
                        element={<GenerateLicense />} 
                        paths={["generatelicense"]} 
                      />
                    } 
                  />

                  <Route 
                    path="purgedata" 
                    element={
                      <ProtectedRoute
                        element={<SubLayout />}
                        paths={["purgedata"]}
                      />
                    }
                  >
                    <Route index element={<PurgeData />} />
                  </Route>
                  
                  <Route 
                    path="dataimporter" 
                    element={
                      <ProtectedRoute
                        element={<SubLayout />}
                        paths={["dataimporter"]}
                      />
                    }
                  >
                    <Route index element={<DataImporter />} />
                  </Route>
                  
                  <Route 
                    path="/datarequests" 
                    element={
                      <ProtectedRoute 
                        element={<DataRequests />} 
                        paths={["datarequests"]} 
                      />
                    } 
                  />

                  {/* Dynamic module (user-facing) */}
                  <Route
                    path="/modules/:collection"
                    element={
                      <ProtectedRoute 
                        element={<SubLayout />} 
                        paths={["modules"]} 
                      />
                    }
                  >
                    <Route index element={<DynamicList />} />
                    <Route path="new" element={<DynamicEdit />} />
                    <Route path=":recordId" element={<DynamicEdit />} />
                  </Route>

                  {/* Dynamic data definitions */}
                  {renderRoute("collections", CollectionDefs, CollectionDefPage, "collectionId")}

                  {/* Forbidden */}
                  <Route path="/403" element={<Forbidden />} />  

                  {/* NotFound */}    
                  <Route path="*" element={<NotFound />} />
                </Route>
              </Routes>    
            </ThemeProvider>          
          </DateRangeProvider>
        </AuthProvider> 
      </BrowserRouter>
    </div>
  );
}

export default App;
